import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import { RootState, AppDispatch } from "../../store/store";

import useModals from "../useModals";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";

import SystemOperationModeService from "../../services/settings/SystemOperationModeService";
import {
    startLoading,
    setSubmitting,
    setConfiguration,
    updateFormField,
    resetState,
    SystemOperationModeForm,
} from "../../store/slices/settings/systemOperationModeSlice";

/**
 * ⚙️ useSystemOperationMode
 * ------------------------------------------------------------
 * Hook centralizado para manejar la configuración del modo
 * de operación del sistema (impresión y envío DTE).
 */
export const useSystemOperationMode = () => {
    const dispatch: AppDispatch = useDispatch();
    const { openModal } = useModals();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();

    const { form, isLoading, isSubmitting, error, success } = useSelector(
        (state: RootState) => state.systemOperationMode
    );

    // =========================================================
    // 🔹 FETCH ACTIVE CONFIGURATION
    // =========================================================
    const fetchActiveConfiguration = async () => {
        try {
            dispatch(startLoading());
            showLoadingIndicator();

            const response = await SystemOperationModeService.getActive();
            const { statusCode,
                // type,
                // title,
                // message,
                data } = response;

            if (statusCode === 200) {
                dispatch(setConfiguration(data));
                // openModal(type, { title, message });
            }
        } catch (err: any) {
            handle(err);
        } finally {
            hideLoadingIndicator();
        }
    };

    // =========================================================
    // 💾 SAVE / UPDATE CONFIGURATION
    // =========================================================
    const handlerSaveConfiguration = async () => {
        try {
            dispatch(setSubmitting(true));
            showLoadingIndicator();

            const payload = { ...form };
            let response;

            if (form.id) {
                // UPDATE
                response = await SystemOperationModeService.update(form.id, payload);
            } else {
                // CREATE
                response = await SystemOperationModeService.create(payload);
            }

            const { statusCode, type, title, message, data } = response;

            if (statusCode === 200 || statusCode === 201) {
                dispatch(setConfiguration(data));
                openModal(type, { title, message });
            }
        } catch (err: any) {
            handle(err);
        } finally {
            hideLoadingIndicator();
            dispatch(setSubmitting(false));
        }
    };

    // =========================================================
    // 🧩 UPDATE FIELD
    // =========================================================
    const updateField = useCallback(
        <K extends keyof SystemOperationModeForm>(field: K, value: SystemOperationModeForm[K]) => {
            dispatch(updateFormField({ field, value }));
        },
        [dispatch]
    );

    // =========================================================
    // ♻️ RESET STATE
    // =========================================================
    const resetFormState = useCallback(() => {
        dispatch(resetState());
    }, [dispatch]);

    return {
        // 🌐 Global state
        form,
        isLoading,
        isSubmitting,
        error,
        success,

        // ⚙️ Actions
        fetchActiveConfiguration,
        handlerSaveConfiguration,
        updateField,
        resetFormState,
    };
};

export default useSystemOperationMode;
